import { FadeUp, SectionHeading } from "./Reveal";

const G = "/projects/gmu";

const PhaseLabel = ({ num, title }) => (
  <div className="flex items-center gap-4 mb-6">
    <span className="font-display text-4xl md:text-5xl font-bold text-[#4361C2]">{num}</span>
    <span className="text-xs tracking-[0.3em] uppercase text-ash border-b border-white/15 pb-1">{title}</span>
  </div>
);

const Caption = ({ children }) => (
  <div className="mt-3 text-[10px] tracking-[0.25em] uppercase text-ash">{children}</div>
);

const Img = ({ src, alt, testId, className = "" }) => (
  <img
    src={src}
    alt={alt}
    loading="lazy"
    className={`w-full object-cover border border-white/15 grayscale hover:grayscale-0 transition-all duration-700 ${className}`}
    data-testid={testId}
  />
);

const FACTS = [
  ["Client", "George Mason University"],
  ["Location", "Fairfax, Virginia"],
  ["Role", "Construction Project Manager"],
  ["System", "Concrete Tilt-Up + Tensile Fabric"],
];

export const CaseStudyGMU = () => (
  <section id="case-study-gmu" className="px-6 md:px-12 py-28 md:py-40" data-testid="gmu-case-study-section">
    <SectionHeading index="Case Study 02 — Construction Project Management" title="Activities Building" testId="gmu-case-study-heading" />

    <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8 mb-20 md:mb-28">
      <FadeUp className="md:col-span-5">
        <dl className="border border-white/15 divide-y divide-white/10" data-testid="gmu-case-study-facts">
          {FACTS.map(([k, v]) => (
            <div key={k} className="flex items-center justify-between gap-4 px-5 py-4">
              <dt className="text-[10px] tracking-[0.3em] uppercase text-ash">{k}</dt>
              <dd className="text-sm font-medium text-right">{v}</dd>
            </div>
          ))}
        </dl>
      </FadeUp>
      <FadeUp delay={0.1} className="md:col-span-6 md:col-start-7">
        <p className="text-lg md:text-xl font-light leading-relaxed text-paper" data-testid="gmu-case-study-intro">
          As a Construction Project Manager with George Mason University, Lonny led the delivery
          of the university's Activities Building — a concrete tilt-up core flanked by two tensile
          tent structures of steel framing and fabric covering, engineered so the university can
          remove the tents later and build permanent facilities in their place.
        </p>
        <p className="mt-5 text-sm leading-relaxed text-ash">
          The hybrid approach saved the school money and compressed the construction timeline —
          flexible infrastructure that serves students now without boxing in the campus master plan.
        </p>
      </FadeUp>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 mb-20 md:mb-28">
      <FadeUp className="md:col-span-12">
        <PhaseLabel num="01" title="The Vision" />
      </FadeUp>
      <FadeUp className="md:col-span-7">
        <Img src={`${G}/rendering.jpg`} alt="Architectural rendering of the Mason Patriots Activities Building entrance" testId="gmu-rendering-image" className="aspect-[16/9]" />
        <Caption>The rendering — Mason Patriots entrance tower</Caption>
      </FadeUp>
      <FadeUp delay={0.1} className="md:col-span-5 md:mt-24">
        <Img src={`${G}/groundbreaking.jpg`} alt="Groundbreaking ceremony with university leadership" testId="gmu-groundbreaking-image" className="aspect-square" />
        <Caption>Groundbreaking — shovels in Virginia clay</Caption>
      </FadeUp>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 mb-20 md:mb-28">
      <FadeUp className="md:col-span-12">
        <PhaseLabel num="02" title="The Tilt-Up" />
      </FadeUp>
      <FadeUp className="md:col-span-8">
        <Img src={`${G}/crane-lift.jpg`} alt="Crane lifting a 50-foot concrete tilt-up panel" testId="gmu-crane-image" className="aspect-[16/10]" />
        <Caption>Twelve 50-foot panels, lifted into place by crane</Caption>
      </FadeUp>
      <FadeUp delay={0.1} className="md:col-span-4 md:mt-24">
        <Img src={`${G}/panel-braced.jpg`} alt="Braced tilt-up concrete panel with crew" testId="gmu-panel-image" className="aspect-[3/4]" />
        <Caption>Panels braced as the core takes shape</Caption>
      </FadeUp>
      <FadeUp className="md:col-span-5">
        <Img src={`${G}/brace-detail.jpg`} alt="Tilt-up panel bracing detail" testId="gmu-brace-image" className="aspect-[4/5]" />
        <Caption>Pipe bracing until panels tie together</Caption>
      </FadeUp>
      <FadeUp delay={0.1} className="md:col-span-7 flex items-center">
        <blockquote className="border-l-2 border-[#4361C2] pl-6 md:pl-8 py-2" data-testid="gmu-case-study-quote">
          <p className="font-display text-2xl md:text-3xl uppercase font-bold leading-tight tracking-tight">
            "Slab poured for the entire site. Twelve 50-ft panels flown by crane. A stormwater retention system underneath it all."
          </p>
        </blockquote>
      </FadeUp>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
      <FadeUp className="md:col-span-12">
        <PhaseLabel num="03" title="The Tents" />
      </FadeUp>
      <FadeUp className="md:col-span-5">
        <Img src={`${G}/tent-interior.jpg`} alt="Steel-framed tensile tent structure interior" testId="gmu-tent-interior-image" className="aspect-[4/5]" />
        <Caption>Steel frames + fabric skin over the courts, with mezzanines</Caption>
      </FadeUp>
      <FadeUp delay={0.1} className="md:col-span-7 md:mt-24">
        <Img src={`${G}/exterior.jpg`} alt="Activities Building exterior with tent steel erected" testId="gmu-exterior-image" className="aspect-[16/10]" />
        <Caption>Tent steel rising around the finished tilt-up core</Caption>
      </FadeUp>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
      <FadeUp className="md:col-span-12">
        <PhaseLabel num="04" title="Delivered" />
      </FadeUp>
      <FadeUp className="md:col-span-12">
        <Img src={`${G}/finished.jpg`} alt="Completed George Mason Activities Building with landscaping" testId="gmu-finished-image" />
        <Caption>The completed Activities Building — open for Mason students</Caption>
      </FadeUp>
      <FadeUp className="md:col-span-8">
        <p className="text-sm leading-relaxed text-ash max-w-2xl" data-testid="gmu-case-study-outro">
          Designed to be un-built: when George Mason is ready for a permanent facility, the tents
          come down and the core remains — proof that the smartest construction management move
          is sometimes planning for what comes after.
        </p>
      </FadeUp>
    </div>
  </section>
);
